import { TranslocoHttpLoader } from '../utils/transloco-http-loader';
import {
  EnvironmentProviders,
  makeEnvironmentProviders,
  inject,
  provideAppInitializer,
} from '@angular/core';
import { TranslocoService, provideTransloco } from '@jsverse/transloco';
import { provideTranslocoMessageformat } from '@jsverse/transloco-messageformat';
import { updateAuthTextMap, updateAuthErrorMap } from '@tmx/auth';
import { firstValueFrom, tap } from 'rxjs';

const DEFAULT_LANG = 'en-US';

function hideEmptyKey(key: string, transloco: TranslocoService) {
  const translation = transloco.translate(key);
  if (translation === key) {
    return '';
  }
  return translation;
}

export const provideRootTranslocoConfig = (): EnvironmentProviders => {
  return makeEnvironmentProviders([
    provideTransloco({
      config: {
        availableLangs: [DEFAULT_LANG],
        defaultLang: DEFAULT_LANG,
        fallbackLang: DEFAULT_LANG,
        reRenderOnLangChange: true,
        prodMode: true,
      },
      loader: TranslocoHttpLoader,
    }),
    provideTranslocoMessageformat(),
    provideAppInitializer(() => {
      const initializerFn = ((transloco: TranslocoService) => {
        return async () =>
          firstValueFrom(
            transloco.load(DEFAULT_LANG).pipe(
              tap(() => {
                updateAuthTextMap({
                  appName: hideEmptyKey('auth.textMap.appName', transloco),
                  appDescription: hideEmptyKey(
                    'auth.textMap.appDescription',
                    transloco
                  ),
                  appTagline: hideEmptyKey(
                    'auth.textMap.appTagLine',
                    transloco
                  ),
                  loginFieldLabel: hideEmptyKey(
                    'auth.textMap.loginFieldLabel',
                    transloco
                  ),
                  loginFieldPlaceholder: hideEmptyKey(
                    'auth.textMap.loginFieldPlaceholder',
                    transloco
                  ),
                  continueButtonText: hideEmptyKey(
                    'auth.textMap.continueButtonText',
                    transloco
                  ),
                  continueButtonLoadingText: hideEmptyKey(
                    'auth.textMap.continueButtonLoadingText',
                    transloco
                  ),
                  accountSelectorLabel: hideEmptyKey(
                    'auth.textMap.accountSelectorLabel',
                    transloco
                  ),
                  redirectText: hideEmptyKey(
                    'auth.textMap.redirectText',
                    transloco
                  ),
                });

                updateAuthErrorMap({
                  accountExpired: hideEmptyKey(
                    'auth.errorMap.accountExpired',
                    transloco
                  ),
                  genericError: hideEmptyKey(
                    'auth.errorMap.genericError',
                    transloco
                  ),
                  loginNotExist: hideEmptyKey(
                    'auth.errorMap.loginNotExist',
                    transloco
                  ),
                  notLicensedForProduct: hideEmptyKey(
                    'auth.errorMap.notLicensedForProduct',
                    transloco
                  ),
                  tidTokenExpired: hideEmptyKey(
                    'auth.errorMap.tidTokenExpired',
                    transloco
                  ),
                  unauthorized: hideEmptyKey(
                    'auth.errorMap.unauthorized',
                    transloco
                  ),
                  usernameNotExist: hideEmptyKey(
                    'auth.errorMap.usernameNotExist',
                    transloco
                  ),
                });
              })
            )
          );
      })(inject(TranslocoService));
      return initializerFn();
    }),
  ]);
};

/**
  t(auth.errorMap.accountExpired)
  t(auth.errorMap.genericError)
  t(auth.errorMap.loginNotExist)
  t(auth.errorMap.notLicensedForProduct)
  t(auth.errorMap.tidTokenExpired)
  t(auth.errorMap.unauthorized)
  t(auth.errorMap.usernameNotExist)
  t(auth.textMap.accountSelectorLabel)
  t(auth.textMap.appDescription)
  t(auth.textMap.appName)
  t(auth.textMap.appTagLine)
  t(auth.textMap.continueButtonLoadingText)
  t(auth.textMap.continueButtonText)
  t(auth.textMap.loginFieldLabel)
  t(auth.textMap.loginFieldPlaceholder)
  t(auth.textMap.redirectText)
*/
